import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Savings,
  CheckCircle,
  Schedule,
  Cancel,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import {
  useGetPayoutRequestsQuery,
  useGetPayoutRequestStatsQuery,
  useApprovePayoutRequestMutation,
  useRejectPayoutRequestMutation,
  useExcludeFromStatsMutation,
} from '../../store/api/adminApi';

const PayoutRequestsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);

  const statusFilter = useMemo(() => {
    return searchParams.get('status') || 'pending';
  }, [searchParams]);

  const setStatusFilterWithUrl = (value: string) => {
    searchParams.set('status', value);
    setSearchParams(searchParams);
    setPage(0);
  };

  const { data, isLoading, error, refetch } = useGetPayoutRequestsQuery({
    status: statusFilter,
    page: page + 1,
    limit: rowsPerPage,
    showExcluded: showExcluded ? 'true' : 'false',
  });

  const { data: statsData } = useGetPayoutRequestStatsQuery({
    showExcluded: showExcluded ? 'true' : 'false',
  });
  const [approvePayoutRequest] = useApprovePayoutRequestMutation();
  const [rejectPayoutRequest] = useRejectPayoutRequestMutation();
  const [excludeFromStats] = useExcludeFromStatsMutation();

  const payoutRequests = (data as any)?.payoutRequests || [];
  const pagination = (data as any)?.pagination || {};

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      await approvePayoutRequest({
        id: selectedRequest.id,
        notes: approvalNotes,
      }).unwrap();
      setApproveDialog(false);
      setSelectedRequest(null);
      setApprovalNotes('');
      refetch();
    } catch (error) {
      console.error('Payout approval failed:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason) return;
    try {
      await rejectPayoutRequest({
        id: selectedRequest.id,
        reason: rejectReason,
      }).unwrap();
      setRejectDialog(false);
      setSelectedRequest(null);
      setRejectReason('');
      refetch();
    } catch (error) {
      console.error('Payout rejection failed:', error);
    }
  };

  const handleExcludeFromStats = async (request: any, exclude: boolean) => {
    try {
      await excludeFromStats({
        type: 'payout_request',
        id: request.id,
        exclude: exclude,
      }).unwrap();
      refetch();
    } catch (error) {
      console.error('Exclude from stats failed:', error);
    }
  };

  const getStatusChip = (request: any) => {
    if (request.status === 'rejected') {
      return (
        <Chip
          icon={<Cancel />}
          label="拒否"
          color="error"
          size="small"
        />
      );
    }
    if (request.status === 'approved' || request.payoutDate) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="処理済み"
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<Schedule />}
        label="申請中"
        color="warning"
        size="small"
      />
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(price);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds ? timestamp._seconds * 1000 : timestamp);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case '1': return '普通';
      case '2': return '当座';
      default: return type;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <Savings sx={{ mr: 2, verticalAlign: 'middle' }} />
          出金申請管理
        </Typography>
      </Box>

      {/* 統計カード */}
      <Box display="flex" gap={2} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              申請中
            </Typography>
            <Typography variant="h4" color="warning.main">
              {statsData?.pending || 0}件
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatPrice(statsData?.pendingAmount || 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              処理済み
            </Typography>
            <Typography variant="h4" color="success.main">
              {statsData?.processed || 0}件
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatPrice(statsData?.processedAmount || 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              拒否
            </Typography>
            <Typography variant="h4" color="error.main">
              {statsData?.rejected || 0}件
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatPrice(statsData?.rejectedAmount || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* フィルター */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={3}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={statusFilter}
              label="ステータス"
              onChange={(e) => setStatusFilterWithUrl(e.target.value)}
            >
              <MenuItem value="pending">申請中</MenuItem>
              <MenuItem value="processed">処理済み</MenuItem>
              <MenuItem value="rejected">拒否</MenuItem>
              <MenuItem value="all">すべて</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={showExcluded}
                onChange={(e) => {
                  setShowExcluded(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="統計除外の取引も表示"
          />
        </Box>
      </Paper>

      {/* ローディング */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* エラー */}
      {error && (
        <Alert severity="error">
          データの取得に失敗しました。
        </Alert>
      )}

      {/* テーブル */}
      {!isLoading && !error && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>申請者</TableCell>
                  <TableCell>銀行口座</TableCell>
                  <TableCell align="right">振込額</TableCell>
                  <TableCell>申請日時</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell align="center">統計除外</TableCell>
                  <TableCell align="center">アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" sx={{ py: 4 }}>
                        {statusFilter === 'pending' ? '申請中の出金申請はありません' :
                         statusFilter === 'processed' ? '処理済みの出金申請はありません' :
                         statusFilter === 'rejected' ? '拒否された出金申請はありません' :
                         '出金申請はありません'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payoutRequests.map((request: any) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {request.userName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.userEmail}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            ID: {request.userId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {request.accountName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            銀行:{request.bankCode} 支店:{request.branchCode}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {getAccountTypeLabel(request.accountType)} {request.accountNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatPrice(request.usedAmmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.createdAt)}
                        </Typography>
                        {request.payoutDate && (
                          <Typography variant="caption" color="success.main" display="block">
                            処理: {formatDate(request.payoutDate)}
                          </Typography>
                        )}
                        {request.rejectedAt && (
                          <Typography variant="caption" color="error.main" display="block">
                            拒否: {formatDate(request.rejectedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(request)}
                        {request.rejectedReason && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                            理由: {request.rejectedReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {request.excludeFromStats ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleExcludeFromStats(request, false)}
                            sx={{ fontSize: '0.7rem', px: 1 }}
                          >
                            統計に含める
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleExcludeFromStats(request, true)}
                            sx={{ fontSize: '0.7rem', px: 1 }}
                          >
                            統計から除外
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!request.payoutDate && request.status !== 'rejected' && (
                          <Box display="flex" gap={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ThumbUp />}
                              onClick={() => {
                                setSelectedRequest(request);
                                setApproveDialog(true);
                              }}
                            >
                              承認
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ThumbDown />}
                              onClick={() => {
                                setSelectedRequest(request);
                                setRejectDialog(true);
                              }}
                            >
                              拒否
                            </Button>
                          </Box>
                        )}
                        {request.csvUrl && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            CSV出力済み
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
          />
        </Paper>
      )}

      {/* 承認ダイアログ */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>出金申請の承認</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>申請者:</strong> {selectedRequest.userName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>振込額:</strong> {formatPrice(selectedRequest.usedAmmount)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>口座名義:</strong> {selectedRequest.accountName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>銀行コード:</strong> {selectedRequest.bankCode} / <strong>支店コード:</strong> {selectedRequest.branchCode}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>口座番号:</strong> {getAccountTypeLabel(selectedRequest.accountType)} {selectedRequest.accountNumber}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Alert severity="info" sx={{ mb: 2 }}>
                この出金申請を承認すると、処理済みとしてマークされます。
                実際の振込処理は手動で行ってください。
              </Alert>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="承認メモ（任意）"
            type="text"
            fullWidth
            variant="outlined"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="承認に関するメモを入力してください"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)}>
            キャンセル
          </Button>
          <Button onClick={handleApprove} color="success" variant="contained">
            承認する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 拒否ダイアログ */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>出金申請の拒否</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>申請者:</strong> {selectedRequest.userName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>振込額:</strong> {formatPrice(selectedRequest.usedAmmount)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Alert severity="warning" sx={{ mb: 2 }}>
                拒否すると、ユーザーの残高に出金額が戻ります。拒否理由は必須です。
              </Alert>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="拒否理由（必須）"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="拒否理由を入力してください"
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason.trim()}
          >
            拒否する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutRequestsPage;
